'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { FilterIcon, XIcon, SearchIcon, CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { UserCombobox } from '@/components/ui/user-combobox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { AuditLogsFilterParams } from '@/types/audit-log'
import { ModuleEnum, AuditActionEnum, AuditResultEnum } from '@/enums'
import { cn } from '@/lib/utils'
import { useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'

type PropsType = {
  filters: AuditLogsFilterParams
  onFiltersChange: (filters: Partial<AuditLogsFilterParams>) => void
  onResetFilters: () => void
}

const Filters = ({ filters, onFiltersChange, onResetFilters }: PropsType) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canListUsers = hasPermission(currentUser, 'USERS', 'LIST')

  const { register, handleSubmit, watch, setValue, reset } = useForm<AuditLogsFilterParams>({
    defaultValues: filters
  })

  useEffect(() => {
    reset(filters)
  }, [filters, reset])

  const watchAll = watch()

  const onSubmit = (data: AuditLogsFilterParams) => {
    onFiltersChange({ ...data, page: 1 })
    setIsOpen(false)
  }

  const handleClearSearch = () => {
    setValue('q', '')
  }

  const formatDateLabel = (value?: string) => (value ? format(new Date(value), 'dd.MM.yyyy') : t('start_date'))

  const handleCalendarChange = (field: 'createdAtStart' | 'createdAtEnd', date?: Date) => {
    setValue(field, date ? date.toISOString() : undefined)
  }

  const moduleOptions: ComboboxOption[] = Object.values(ModuleEnum).map(v => ({
    label: t(`module_${v}` as any),
    value: v,
    searchValue: t(`module_${v}` as any)
  }))

  const actionOptions: ComboboxOption[] = Object.values(AuditActionEnum).map(v => ({
    label: t(`audit_action_${v}` as any),
    value: v,
    searchValue: t(`audit_action_${v}` as any)
  }))

  const resultOptions: ComboboxOption[] = [
    { label: t('audit_result_success'), value: AuditResultEnum.SUCCESS },
    { label: t('audit_result_failed'), value: AuditResultEnum.FAILED }
  ]

  const hasActiveFilters = () => {
    return (
      watchAll.q ||
      (watchAll.userIds && watchAll.userIds.length > 0) ||
      (watchAll.modules && watchAll.modules.length > 0) ||
      (watchAll.actions && watchAll.actions.length > 0) ||
      (watchAll.results && watchAll.results.length > 0) ||
      watchAll.createdAtStart ||
      watchAll.createdAtEnd
    )
  }

  return (
    <div className="flex items-center justify-end">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="outline">
            <FilterIcon className="h-4 w-4" />
            <span className="hidden flex-row items-center justify-items-center lg:flex">{t('filters')}</span>
            {hasActiveFilters() && (
              <span className="bg-primary text-primary-foreground flex h-5 w-5 items-center justify-center rounded-full text-xs">
                !
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('filter_records')}</SheetTitle>
            <SheetDescription>{t('filter_description')}</SheetDescription>
          </SheetHeader>

          <ScrollArea>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-4">
              {/** KEYWORD */}
              <div className="space-y-2">
                <Label htmlFor="filter-q">{t('keyword')}</Label>
                <div className="relative">
                  <Input
                    placeholder={t('keyword')}
                    id="filter-q"
                    {...register('q')}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSubmit(onSubmit)()
                      }
                    }}
                    icon={<SearchIcon className="h-4 w-4" />}
                    className="pr-8"
                  />
                  {watchAll.q && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1.5 right-1 h-6 w-6 p-0"
                      onClick={handleClearSearch}
                      type="button">
                      <XIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/** USER */}
              {canListUsers && (
                <div className="space-y-2">
                  <Label>{t('user')}</Label>
                  <UserCombobox
                    value={watchAll.userIds || []}
                    onValueChange={value => setValue('userIds', value as string[])}
                    multiple
                    clearable
                  />
                </div>
              )}

              {/** MODULE */}
              <div className="space-y-2">
                <Label>{t('module')}</Label>
                <Combobox
                  options={moduleOptions}
                  value={watchAll.modules || []}
                  onValueChange={value => setValue('modules', value as ModuleEnum[])}
                  placeholder={t('select')}
                  multiple
                  clearable
                />
              </div>

              {/** ACTION */}
              <div className="space-y-2">
                <Label>{t('action')}</Label>
                <Combobox
                  options={actionOptions}
                  value={watchAll.actions || []}
                  onValueChange={value => setValue('actions', value as AuditActionEnum[])}
                  placeholder={t('select')}
                  multiple
                  clearable
                />
              </div>

              {/** RESULT */}
              <div className="space-y-2">
                <Label>{t('result')}</Label>
                <Combobox
                  options={resultOptions}
                  value={watchAll.results || []}
                  onValueChange={value => setValue('results', value as AuditResultEnum[])}
                  placeholder={t('select')}
                  multiple
                  clearable
                />
              </div>

              {/** CREATED DATE RANGE */}
              <div className="space-y-2">
                <Label>{t('created_date_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(['createdAtStart', 'createdAtEnd'] as const).map(field => (
                    <Popover key={field}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'justify-start gap-1 text-left font-normal',
                            !watchAll[field] && 'text-muted-foreground'
                          )}>
                          <CalendarIcon className="mr-1 h-4 w-4" />
                          {watchAll[field]
                            ? formatDateLabel(watchAll[field])
                            : field === 'createdAtStart'
                              ? t('start_date')
                              : t('end_date')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          captionLayout="dropdown"
                          startMonth={new Date(1900, 0)}
                          endMonth={new Date()}
                          mode="single"
                          selected={watchAll[field] ? new Date(watchAll[field]) : undefined}
                          onSelect={date => handleCalendarChange(field, date || undefined)}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </div>
              </div>

              <div className="flex flex-row gap-2">
                <Button type="submit" variant="default" className="flex-1">
                  <FilterIcon className="h-4 w-4" />
                  {t('filter')}
                </Button>
                <Button
                  onClick={() => {
                    reset()
                    onResetFilters()
                  }}
                  variant="destructive"
                  className="flex-1"
                  type="button">
                  <XIcon className="h-4 w-4" />
                  {t('clear_filters')}
                </Button>
              </div>
            </form>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default Filters
